import { useCallback, useEffect, useRef, useState } from 'react';
import './BeforeAfter.css';

/**
 * BeforeAfter · Interaktiver Vorher/Nachher-Bild-Slider.
 *
 * Der User zieht einen vertikalen Divider horizontal über das Bild:
 *  - links vom Divider → Nachher
 *  - rechts vom Divider → Vorher
 * Unterstützt Maus, Touch und Tastatur (← → = 5 %, Home/End = 0 / 100 %).
 *
 * Props:
 *   before / after      – Bild-URLs
 *   beforeAlt / afterAlt – a11y alt-Texte
 *   initial             – Start-Position in Prozent (default 50)
 *   onDragStart / onDragEnd – Callbacks, damit ein Außen-Carousel seinen
 *                            Autoplay pausieren kann, solange User dragt
 */
export default function BeforeAfter({
  before,
  after,
  beforeAlt = 'Vorher',
  afterAlt = 'Nachher',
  initial = 50,
  onDragStart,
  onDragEnd,
}) {
  const [split, setSplit] = useState(initial);
  const wrapperRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromX = useCallback((clientX) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSplit(pct);
  }, []);

  const startDrag = useCallback(
    (clientX) => {
      draggingRef.current = true;
      onDragStart?.();
      updateFromX(clientX);
    },
    [updateFromX, onDragStart]
  );

  const endDrag = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onDragEnd?.();
  }, [onDragEnd]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return;
      updateFromX(e.clientX);
    };
    const onTouchMove = (e) => {
      if (!draggingRef.current) return;
      updateFromX(e.touches[0].clientX);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', endDrag);
      window.removeEventListener('touchcancel', endDrag);
    };
  }, [updateFromX, endDrag]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setSplit((s) => Math.max(0, s - 5));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setSplit((s) => Math.min(100, s + 5));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSplit(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setSplit(100);
    }
  };

  return (
    <div
      className="ba"
      ref={wrapperRef}
      onMouseDown={(e) => startDrag(e.clientX)}
      onTouchStart={(e) => startDrag(e.touches[0].clientX)}
      style={{ '--ba-split': `${split}%` }}
    >
      {/* Basis-Bild = "Nachher" (komplett sichtbar) */}
      <img
        className="ba__img"
        src={after}
        alt={afterAlt}
        draggable={false}
      />

      {/* Darüber gelegt = "Vorher", per clip-path von rechts eingeblendet */}
      <div className="ba__clip" aria-hidden="true">
        <img
          className="ba__img"
          src={before}
          alt=""
          draggable={false}
        />
      </div>

      {/* Labels */}
      <span className="ba__badge ba__badge--before">Vorher</span>
      <span className="ba__badge ba__badge--after">Nachher</span>

      {/* Divider + Handle */}
      <div className="ba__divider" aria-hidden="true" />
      <button
        type="button"
        className="ba__handle"
        role="slider"
        aria-label="Vorher-Nachher vergleichen"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(split)}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => {
          e.stopPropagation();
          startDrag(e.clientX);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          startDrag(e.touches[0].clientX);
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
          <polyline points="9 18 15 12 9 6" transform="translate(6,0)" />
        </svg>
      </button>
    </div>
  );
}
